import { APP } from '../tokens';

export type RiskReviewNudgeLevel = 'attention' | 'reconsider';

export interface RiskReviewEntryBannerProps {
  variant: 'compact' | 'chatCard';
  nudgeLevel: RiskReviewNudgeLevel;
  /** Short headline (e.g. risk summary label) */
  label: string;
  laneLabel?: string | null;
  jurisdictionLabel?: string | null;
  /** Overrides default disclaimer line on chat variant */
  disclaimer?: string;
  ctaLabel: string;
  onPrimary: () => void;
}

export default function RiskReviewEntryBanner({
  variant,
  nudgeLevel,
  label,
  laneLabel,
  jurisdictionLabel,
  disclaimer,
  ctaLabel,
  onPrimary,
}: RiskReviewEntryBannerProps) {
  const F = APP.fontFamily;
  const isReconsider = nudgeLevel === 'reconsider';

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={onPrimary}
        title={label}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 8px',
          borderRadius: 4,
          backgroundColor: isReconsider ? 'color-mix(in srgb, #D97645 16%, var(--app-bg-version-card))' : 'color-mix(in srgb, #D97645 10%, var(--app-bg-version-card))',
          border: `1px solid ${isReconsider ? '#D97645' : 'rgba(217, 118, 69, 0.55)'}`,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <svg width={10} height={10} viewBox="0 0 24 24" fill="none"
          stroke={isReconsider ? '#C04A2A' : '#D97645'}
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span style={{
          fontFamily: F, fontSize: 10, fontWeight: 500,
          color: isReconsider ? '#C04A2A' : '#D97645',
          lineHeight: '14px', whiteSpace: 'nowrap',
        }}>
          Risk snapshot
        </span>
      </button>
    );
  }

  const bgColor = isReconsider ? '#FFF3EE' : '#FFF8F0';
  const borderColor = isReconsider ? '#E8C4B0' : '#F0DBC8';
  const iconColor = isReconsider ? '#C04A2A' : '#D97645';
  const titleColor = isReconsider ? '#A83A1F' : '#C0612A';
  const ctaColor = isReconsider ? '#C04A2A' : '#D97645';
  const ctaBg = isReconsider ? '#FCDDD3' : '#FDEEE4';

  const metaBits = [laneLabel, jurisdictionLabel].filter(Boolean).join(' · ');
  const subDefault = isReconsider
    ? 'Consider reviewing before proceeding — not legal advice'
    : 'Flagged for your attention — not legal advice';
  const subLine = [metaBits, disclaimer ?? subDefault].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={onPrimary}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 12px',
        borderRadius: 6,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, flexGrow: 1 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: titleColor, fontFamily: F, lineHeight: '16px' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, fontWeight: 400, color: '#9E8B78', fontFamily: F, lineHeight: '14px', textAlign: 'left' }}>
          {subLine}
        </span>
      </div>
      <span style={{
        fontSize: 10,
        fontWeight: isReconsider ? 600 : 500,
        color: ctaColor,
        fontFamily: F,
        flexShrink: 0,
        borderRadius: 4,
        padding: '3px 8px',
        backgroundColor: ctaBg,
      }}>
        {ctaLabel}
      </span>
    </button>
  );
}
